import React, { useEffect, useState } from 'react';
import FormWrapper from '../../Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputField from '../../Form/components/InputField';
import SubmitButton from '../../Button/SubmitButton';
import apiClient from '../../../Services/ApiClient';
import { useAppContext } from '../../../Services/AppContext';
import ScrollContainer from '../../../Modules/Base/Components/ScrollContainer';
// @ts-ignore
import styles from './SearchMedicamentRecordAuthorization.module.scss';
import { Meta, useLazyMyMetaQuery } from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import useApiClient from '../../../Modules/Base/Hooks/useApiClient';
import PasswordField from '../../../Modules/Base/Components/Inputs/PasswordField/PasswordField';
import LinkButton from '../../Button/LinkButton';
import { Theme } from '../../../Enums/Theme.enum';
import SUKLCredentialsHintModal from '../../../Modules/EPrescription/Components/SUKLCredentialsHint/SUKLCredentialsHintModal';
import CheckboxField from '../../Form/components/CheckboxField';
import InputWrapper from '../../Form/components/InputWrapper/InputWrapper';
import InfoTooltip from '../../Popup/InfoTooltip';
import Text from '../../Base/Text/Text';
import { saveToUserMeta } from '../../../Modules/User/Services/userMeta.service';
import { MetaKey } from '../../../Modules/Meta/Enums/MetaKey.enum';
import { findInMeta } from '../../../Modules/Meta/Services/meta.service';

export enum SearchMedicamentRecordAuthorizationSource {
	ERECEPT = 'erecept',
	LEKOVY_ZAZNAM = 'lekovy-zaznam',
}

interface Props {
	setIsLoading: (isLoading: boolean) => void;
	onSuccess: () => void;
	source: SearchMedicamentRecordAuthorizationSource;
}

interface SUKLCredentialsValues {
	uuid?: string;
	password: string;
	remember: boolean;
}

const SearchMedicamentRecordAuthorization: React.FC<Props> = ({
	onSuccess,
	setIsLoading,
	source,
}) => {
	const [SUKLLogin, setSUKLLogin] = useState(undefined);
	const [isHintOpen, setIsHintOpen] = useState(false);
	const { userData, isMou } = useAppContext();
	const [getMyMeta] = useLazyMyMetaQuery();
	const { displayError } = useApiClient();

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const data = values as SUKLCredentialsValues;
		const uuid = data.uuid?.trim()?.length ? data.uuid : SUKLLogin;
		try {
			const result = await apiClient.post(`/sukl/test-cert?source=${source}`, {
				suklLogin: uuid,
				suklPassword: data.password,
			});

			if (result.status === 200) {
				if (data.uuid) {
					await saveToUserMeta({
						userId: userData.id,
						key: MetaKey.SUKL_LOGIN,
						value: uuid,
					});
				}
				if (data.password) {
					await saveToUserMeta({
						userId: userData.id,
						key: MetaKey.SUKL_PASSWORD,
						value: data.password,
					});
				}
				if (!data.remember) {
					await saveToUserMeta({
						userId: userData.id,
						key: MetaKey.SUKL_LOGIN_TEMPORARY,
						value: true,
					});
				}
				onSuccess();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const validationSchema = yup.object({
		uuid: !SUKLLogin
			? yup
					.string()
					.required('validation.searchMedicamentRecordAuthorization.uuid')
			: yup.mixed().notRequired(),
		password: yup
			.string()
			.required('validation.searchMedicamentRecordAuthorization.password'),
		remember: yup.boolean(),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	useEffectAsync(async () => {
		const data = await getMyMeta().unwrap();
		const SUKLLogin = findInMeta({
			meta: data?.me?.meta as Meta[],
			metaKey: MetaKey.SUKL_LOGIN,
		});
		if (SUKLLogin?.value) {
			setSUKLLogin(SUKLLogin.value);
		}
	}, []);

	useEffect(() => {
		const submit = (e: any) => {
			if (e.keyCode === 13) {
				handleSubmit(formMethods.getValues());
			}
		};
		// @ts-ignore
		window.addEventListener('keydown', submit);
		// @ts-ignore
		return () => window.removeEventListener('keydown', submit);
	}, []);

	return (
		<ScrollContainer className={styles.wrapper}>
			<div className='row'>
				<div className='col-lg-6 col-12'>
					<FormWrapper formMethods={formMethods}>
						{!SUKLLogin && (
							<InputField
								name='uuid'
								label='component.certificates.enterYourLogin'
								className='mb-3'
								isRequired
							/>
						)}
						<InputWrapper>
							<PasswordField
								name='password'
								label='component.searchMedicamentRecordAuthorization.password'
								isRequired
							/>
						</InputWrapper>
						{!isMou && (
							<InputWrapper>
								<div className={styles.remember}>
									<CheckboxField
										name='remember'
										tx='component.searchMedicamentRecordAuthorization.remember'
									/>
									<InfoTooltip>
										<Text tx='component.searchMedicamentRecordAuthorization.rememberHint' />
									</InfoTooltip>
								</div>
							</InputWrapper>
						)}
						<div className={styles.controls}>
							<SubmitButton
								onSubmit={handleSubmit}
								title='base.shared.continue'
							/>
							<LinkButton
								title='component.certificates.whereToGetSUKLLogin'
								theme={Theme.BLUE}
								onClick={() => setIsHintOpen(true)}
							/>
						</div>
					</FormWrapper>
				</div>
			</div>
			{isHintOpen && (
				<SUKLCredentialsHintModal
					onClose={() => setIsHintOpen(false)}
					onConfirm={() => setIsHintOpen(false)}
				/>
			)}
		</ScrollContainer>
	);
};

export default SearchMedicamentRecordAuthorization;
